import React, { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Save, Copy, Trash2 } from "lucide-react";

const TEMPLATES = [
  { id: "primeiro-contato", title: "Primeiro contato", content: "Olá, {nome}! Tudo bem? Vi que você está precisando de revisão de {tipo}. Trabalho com revisão há anos e ficarei feliz em ajudar. Me conta mais sobre o trabalho — quantidade de páginas, prazo e área? Daí já te envio um orçamento sob medida. :)" },
  { id: "follow-up", title: "Follow-up", content: "Oi, {nome}! Só passando pra saber se você teve a chance de dar uma olhada na proposta que enviei. Qualquer dúvida, é só me chamar — posso ajustar prazo, escopo ou condição de pagamento. Estou à disposição!" },
  { id: "objecao-preco", title: "Objeção de preço", content: "Entendo completamente. O valor reflete o tempo de dedicação, minha formação e a responsabilidade com o seu trabalho. Posso oferecer 3 opções: (1) dividir em 2 parcelas; (2) revisar apenas ortografia/gramática em vez de revisão completa, reduzindo o escopo; (3) manter o preço e te garantir 1 rodada extra de ajustes. Qual faz mais sentido pra você?" },
  { id: "pos-entrega", title: "Pós-entrega (pedir indicação)", content: "Foi um prazer trabalhar com você, {nome}! Espero que o material atenda expectativas. Se conhecer alguém que também precisa de revisão — colegas, orientandos, amigos — sua indicação faz toda a diferença pra mim. Um abraço!" },
];

export default function Scripts() {
  const { user } = useAuth();
  const [scripts, setScripts] = useState([]);
  const [form, setForm] = useState({ title: "", content: "" });

  useEffect(() => {
    if (!user) return;
    const raw = localStorage.getItem(`scripts_${user.id}`);
    setScripts(raw ? JSON.parse(raw) : []);
  }, [user]);

  const save = (arr) => {
    localStorage.setItem(`scripts_${user.id}`, JSON.stringify(arr));
    setScripts(arr);
  };

  const add = () => {
    if (!form.title.trim() || !form.content.trim()) return toast.error("Preencha título e conteúdo");
    save([{ id: Date.now().toString(), ...form }, ...scripts]);
    setForm({ title: "", content: "" });
    toast.success("Script salvo");
  };
  const remove = (id) => save(scripts.filter((s) => s.id !== id));
  const copy = (t) => { navigator.clipboard.writeText(t); toast.success("Copiado!"); };
  const applyTemplate = (tpl) => setForm({ title: tpl.title, content: tpl.content });

  return (
    <div data-testid="scripts-page">
      <div className="mb-8">
        <div className="font-mono text-xs uppercase tracking-widest text-[#D9381E] mb-2">Scripts</div>
        <h1 className="font-serif text-4xl md:text-5xl">Scripts Comerciais</h1>
        <p className="text-[#4A4A4A] mt-2">Mensagens prontas para copiar e colar. Edite os templates ou crie os seus.</p>
      </div>

      <div className="grid md:grid-cols-5 gap-6">
        <Card className="md:col-span-2 p-6 bg-white border border-black/10 rounded-none space-y-4">
          <div className="font-mono text-xs uppercase tracking-widest text-[#8A8A8A]">Templates prontos</div>
          <div className="space-y-2">
            {TEMPLATES.map((t) => (
              <button key={t.id} onClick={() => applyTemplate(t)} className="w-full text-left p-3 border border-black/10 hover:bg-[#EFECE6] transition-colors">
                <div className="font-serif text-base">{t.title}</div>
                <div className="text-xs text-[#8A8A8A] mt-1 line-clamp-2">{t.content}</div>
              </button>
            ))}
          </div>
        </Card>

        <Card className="md:col-span-3 p-6 bg-white border border-black/10 rounded-none">
          <div className="space-y-3">
            <div><Label className="text-xs font-mono uppercase">Título</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="rounded-none" data-testid="script-title" /></div>
            <div><Label className="text-xs font-mono uppercase">Mensagem</Label>
              <Textarea value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} rows={10} className="rounded-none font-sans text-sm leading-relaxed" data-testid="script-content" /></div>
            <div className="flex gap-2">
              <Button onClick={add} className="rounded-none bg-[#D9381E] hover:bg-[#B82914] text-white" data-testid="script-save-btn">
                <Save className="w-4 h-4 mr-2" /> Salvar na biblioteca
              </Button>
              <Button onClick={() => copy(form.content)} variant="outline" className="rounded-none"><Copy className="w-4 h-4" /></Button>
            </div>
          </div>
        </Card>
      </div>

      {scripts.length > 0 && (
        <div className="mt-10">
          <div className="font-mono text-xs uppercase tracking-widest text-[#8A8A8A] mb-4">Minha biblioteca</div>
          <div className="grid md:grid-cols-2 gap-4">
            {scripts.map((s) => (
              <Card key={s.id} className="p-5 bg-white border border-black/10 rounded-none">
                <div className="flex items-start justify-between gap-2 mb-2">
                  <h3 className="font-serif text-xl">{s.title}</h3>
                  <div className="flex gap-2">
                    <button onClick={() => copy(s.content)} className="text-[#1A1A1A] hover:text-[#D9381E]"><Copy className="w-4 h-4" /></button>
                    <button onClick={() => remove(s.id)} className="text-[#D9381E]"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
                <pre className="whitespace-pre-wrap text-xs font-sans text-[#4A4A4A] line-clamp-4">{s.content}</pre>
              </Card>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
